import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookingCalendar } from '../home/components/BookingCalendar';

const TRUST_BADGES = [
  { icon: 'ri-shield-check-fill', text: 'No obligation, ever' },
  { icon: 'ri-time-fill', text: 'Only 15 minutes' },
  { icon: 'ri-lock-fill', text: 'Your info is private' },
  { icon: 'ri-heart-fill', text: '100% free consultation' },
];

const WHAT_YOU_GET = [
  {
    icon: 'ri-search-eye-line',
    title: 'Personalized Coverage Review',
    desc: 'We analyze exactly how much coverage your family needs — no guessing, no overselling.',
  },
  {
    icon: 'ri-funds-line',
    title: 'Buy Term & Invest the Difference',
    desc: 'Learn how replacing expensive whole life with affordable term + a Roth IRA can build real wealth.',
  },
  {
    icon: 'ri-stack-line',
    title: 'Policy Laddering Strategy',
    desc: 'Discover how to use multiple policies at different life stages to maximize protection and savings.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'A Clear Action Plan',
    desc: 'Walk away with a simple, written next step — no pressure, no jargon, just clarity.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Marcus T.',
    location: 'Indianapolis, IN',
    quote: 'I had no idea I was overpaying for whole life. After my call with Hoosier, I switched to term and started investing the difference. Best financial decision I\'ve made.',
    stars: 5,
  },
  {
    name: 'Danielle R.',
    location: 'Carmel, IN',
    quote: 'The call was genuinely educational — zero pressure. They explained everything in plain English and helped me understand what my family actually needs.',
    stars: 5,
  },
  {
    name: 'Kevin & Aisha M.',
    location: 'Fishers, IN',
    quote: 'We booked the call just to ask a few questions. We ended up with a full wealth strategy. Hoosier is the real deal.',
    stars: 5,
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function WealthStrategyCallPage() {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/wealth-strategy-call#webpage`,
    url: `${SITE_URL}/wealth-strategy-call`,
    name: 'Wealth Strategy Call - Hoosier Life Insurance',
    description: 'Schedule a comprehensive wealth strategy consultation to discuss life insurance, annuities, and generational wealth building strategies for Indiana families.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    datePublished: '2024-01-15T00:00:00-05:00',
    dateModified: new Date().toISOString(),
    breadcrumb: { '@id': `${SITE_URL}/wealth-strategy-call#breadcrumb` }
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${SITE_URL}/wealth-strategy-call#service`,
    name: 'Wealth Strategy Consultation',
    description: 'Comprehensive financial planning consultation covering life insurance, annuities, retirement planning, and generational wealth strategies.',
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: {
      '@type': 'State',
      name: 'Indiana'
    },
    serviceType: 'Financial Planning Consultation'
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${SITE_URL}/wealth-strategy-call#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Wealth Strategy Call',
        item: `${SITE_URL}/wealth-strategy-call`
      }
    ]
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is a Wealth Strategy Call?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Wealth Strategy Call is a free 15-minute consultation with a licensed Hoosier Life Insurance advisor. We review your current coverage, explain the Buy Term &amp; Invest the Difference strategy, and give you a clear, personalized action plan — with zero pressure to buy anything.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is the Wealth Strategy Call really free?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, 100% free. There is no cost, no obligation, and no products are sold during the call. It is a purely educational session designed to help Indianapolis and Indiana families make smarter financial decisions.',
        },
      },
      {
        '@type': 'Question',
        name: 'What topics are covered in the Wealth Strategy Call?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We cover personalized coverage needs, the Buy Term &amp; Invest the Difference strategy, policy laddering for different life stages, Roth IRA basics, and how to build generational wealth using affordable term life insurance as a foundation.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long is the Wealth Strategy Call?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The call is approximately 15 minutes. It is designed to be concise and actionable so you walk away with clarity and a written next step — not a lengthy sales pitch.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need to buy anything after the Wealth Strategy Call?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely not. There is zero obligation to purchase any product after the call. Our goal is to educate you so you can make the best decision for your family — whether that means working with us or not.',
        },
      },
    ],
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to Book a Free Wealth Strategy Call with Hoosier Life Insurance',
    description: 'A simple 3-step process to book your free 15-minute wealth strategy call with a licensed Indianapolis life insurance advisor. No obligation, no products sold.',
    totalTime: 'PT15M',
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: '0',
    },
    supply: [],
    tool: [],
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Pick a Date & Time',
        text: 'Use the booking calendar on this page to select a date and time that works for you. Available slots are shown in real time. The call is 15 minutes and can be done by phone or virtually.',
        url: `${SITE_URL}/wealth-strategy-call`,
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Enter Your Contact Details',
        text: 'Provide your name, email, and phone number so your licensed Hoosier Life Insurance advisor can confirm your appointment and reach you at the scheduled time. Your information is kept private and never sold.',
        url: `${SITE_URL}/wealth-strategy-call`,
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Attend Your Free 15-Minute Call',
        text: 'A licensed advisor will walk you through your personalized coverage needs, explain the Buy Term & Invest the Difference strategy, and give you a clear written action plan. There is zero pressure to purchase anything — this call is 100% educational.',
        url: `${SITE_URL}/wealth-strategy-call`,
      },
    ],
  };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Free Wealth Strategy Call | Life Insurance & Wealth Planning Indianapolis | Hoosier Life';

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setCanonical = (href: string) => {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
      el.setAttribute('href', href);
    };

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.setAttribute('type', 'application/ld+json');
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    setMeta('description', 'Book a free Wealth Strategy Call with Benjamin Wright. Personalized life insurance and wealth-building plan for your family. Call (317) 648-2983.');
    setMeta('keywords', 'free life insurance consultation Indianapolis, wealth strategy call Indianapolis, buy term invest difference Indianapolis, financial planning Indianapolis Indiana');
    setCanonical(`${SITE_URL}/wealth-strategy-call`);
    setMeta('og:title', 'Free Wealth Strategy Call | Life Insurance Consultation Indianapolis', true);
    setMeta('og:description', 'Book a free Wealth Strategy Call with Benjamin Wright. Personalized life insurance and wealth-building plan for your family. Call (317) 648-2983.', true);
    setMeta('og:url', `${SITE_URL}/wealth-strategy-call`, true);
    setMeta('twitter:title', 'Free Wealth Strategy Call | Life Insurance Consultation Indianapolis');
    setMeta('twitter:description', 'Book a free Wealth Strategy Call with Benjamin Wright. Personalized life insurance and wealth-building plan for your family.');
    setMeta('last-modified', new Date().toISOString());

    injectSchema('schema-webpage-wsc', webPageSchema);
    injectSchema('schema-service-wsc', serviceSchema);
    injectSchema('schema-breadcrumb-wsc', breadcrumbSchema);
    injectSchema('schema-faq-wsc', faqSchema);
    injectSchema('schema-howto-wsc', howToSchema);

    // Set a timeout to detect if iframe fails to load
    const loadTimeout = setTimeout(() => {
      if (iframeLoading) {
        setIframeError(true);
        setIframeLoading(false);
      }
    }, 3000); // 3 second timeout — show fallback fast if CORS blocks the iframe

    return () => {
      document.title = prevTitle;
      ['schema-webpage-wsc', 'schema-service-wsc', 'schema-breadcrumb-wsc', 'schema-faq-wsc', 'schema-howto-wsc'].forEach(id => {
        document.getElementById(id)?.remove();
      });
      clearTimeout(loadTimeout);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">

      {/* ── Minimal Header ── */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shield-check-fill text-[18px] text-[#D97642]"></i>
              </div>
              <span className="text-lg font-bold text-[#1A3A52] tracking-tight whitespace-nowrap">
                Hoosier Life Insurance
              </span>
            </div>
          </Link>
          <a
            href="tel:+13176482983"
            className="flex items-center gap-2 text-[14px] font-semibold text-[#1A3A52] hover:text-[#D97642] transition-colors whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-[16px]"></i>
            </div>
            (317) 648-2983
          </a>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/afe711f0ea054aa84b11647d01c75b68.jpg"
            alt="Free Wealth Strategy Call — Hoosier Life Insurance Indianapolis"
            title="Free Wealth Strategy Call with a Licensed Indianapolis Life Insurance Agent"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1400}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/90 via-[#1A3A52]/75 to-[#1A3A52]/40"></div>
        </div>

        <div className="relative max-w-[1100px] mx-auto px-6 py-20 md:py-28">
          <div className="max-w-[600px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-[12px] font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-check-fill text-[13px]"></i>
              </div>
              Free · 15 Minutes · No Obligation
            </span>

            <h1 className="text-[38px] sm:text-[48px] md:text-[56px] font-extrabold text-white leading-[1.1] mb-6">
              Book Your Free<br />
              <span className="text-[#D97642]">Wealth Strategy Call</span>
            </h1>

            <p className="text-[17px] md:text-[19px] text-white/85 leading-[1.7] mb-8 max-w-[520px]">
              In just 15 minutes, discover how middle-income families in Indianapolis are using term life insurance as the foundation of a generational wealth system — and how you can too.
            </p>

            <div className="flex flex-wrap gap-3">
              {TRUST_BADGES.map((badge) => (
                <div key={badge.text} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-[#D97642] text-[14px]`}></i>
                  </div>
                  <span className="text-[13px] text-white font-medium whitespace-nowrap">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <section className="max-w-[1100px] mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-12 xl:gap-16 items-start">

          {/* Left Column */}
          <div>
            {/* What You'll Get */}
            <div className="mb-12">
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">What's Included</span>
              <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-8 leading-tight">
                What You'll Walk Away With
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {WHAT_YOU_GET.map((item) => (
                  <div key={item.title} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D97642]/10 mb-4">
                      <i className={`${item.icon} text-[22px] text-[#D97642]`}></i>
                    </div>
                    <h3 className="text-[15px] font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                    <p className="text-[13px] text-[#4A5568] leading-[1.65]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Wealth Concept Callout */}
            <div className="bg-[#1A3A52] rounded-2xl p-8 mb-12 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#D97642]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-lightbulb-flash-fill text-[20px] text-[#D97642]"></i>
                  </div>
                  <span className="text-[13px] font-semibold text-[#D97642] uppercase tracking-widest">The Key Insight</span>
                </div>
                <h3 className="text-[22px] md:text-[26px] font-bold text-white mb-4 leading-tight">
                  Most families overpay for life insurance by <span className="text-[#D97642]">$300–$600/year</span> — and don't even know it.
                </h3>
                <p className="text-[15px] text-white/80 leading-[1.7] mb-5">
                  Whole life policies are sold as "investments," but the math rarely works in your favor. By switching to affordable term life and investing the difference in a Roth IRA, a 30-year-old can build <strong className="text-white">$400,000+ in tax-free wealth</strong> by retirement.
                </p>
                <div className="flex items-center gap-2 text-[14px] text-[#D97642] font-semibold">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-circle-fill text-[18px]"></i>
                  </div>
                  We'll show you exactly how on your call.
                </div>
              </div>
            </div>

            {/* Testimonials */}
            <div>
              <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Results</span>
              <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-8 leading-tight">
                What Indianapolis Families Are Saying
              </h2>
              <div className="flex flex-col gap-5">
                {TESTIMONIALS.map((t) => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-[#D97642] text-[14px]"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-[14px] text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-[13px] font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-[12px] text-[#718096]">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column — Sticky Booking */}
          <div className="lg:sticky lg:top-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Card Header */}
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-[20px] text-[#D97642]"></i>
                  </div>
                  <div>
                    <h2 className="text-[18px] font-bold text-white leading-tight">Free Wealth Strategy Call</h2>
                    <p className="text-[13px] text-white/70">15 min · Phone or Virtual</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-[11px] font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No products sold</span>
                  <span className="bg-white/10 text-white/90 text-[11px] font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ 100% educational</span>
                  <span className="bg-white/10 text-white/90 text-[11px] font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Zero pressure</span>
                </div>
              </div>

              {/* Booking Calendar */}
              <div className="px-7 py-7">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerEmail: 'Email Address',
                    customerPhone: 'Phone Number',
                    notes: 'Anything you\'d like us to know? (Optional)',
                    submitButton: 'Confirm My Free Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>

              {/* Divider */}
              <div className="px-7 pb-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-100"></div>
                  <span className="text-[12px] text-[#718096] font-medium">or reach out directly</span>
                  <div className="flex-1 h-px bg-gray-100"></div>
                </div>
              </div>

              {/* GHL Quick Contact Form Embed with Error Handling */}
              <div className="px-7 pb-7 pt-4">
                {iframeError ? (
                  // Fallback contact options when iframe fails
                  <div className="bg-[#F9F7F4] rounded-xl p-6 border border-gray-200">
                    <div className="text-center mb-5">
                      <div className="w-14 h-14 bg-[#D97642]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-phone-line text-[28px] text-[#D97642]"></i>
                      </div>
                      <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2">Let's Connect</h3>
                      <p className="text-[13px] text-[#4A5568]">Choose your preferred way to reach us</p>
                    </div>
                    
                    <div className="flex flex-col gap-3">
                      <a
                        href="tel:+13176482983"
                        className="flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-lg hover:bg-[#C26638] transition-all cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-phone-fill text-[18px]"></i>
                        </div>
                        <div className="text-left">
                          <div className="text-[12px] opacity-90">Call us now</div>
                          <div className="text-[15px] font-bold">(317) 648-2983</div>
                        </div>
                      </a>

                      <a
                        href="mailto:info@hoosierlifeinsurance.com"
                        className="flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg hover:border-[#D97642] hover:bg-[#F9F7F4] transition-all cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-mail-line text-[18px]"></i>
                        </div>
                        <div className="text-left">
                          <div className="text-[12px] text-[#718096]">Email us</div>
                          <div className="text-[14px] font-semibold">info@hoosierlifeinsurance.com</div>
                        </div>
                      </a>

                      <button
                        type="button"
                        onClick={() => {
                          const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
                          if (btn) btn.click();
                        }}
                        className="flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg hover:border-[#D97642] hover:bg-[#F9F7F4] transition-all cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-chat-3-line text-[18px]"></i>
                        </div>
                        <div className="text-left">
                          <div className="text-[12px] text-[#718096]">Chat with us</div>
                          <div className="text-[14px] font-semibold">Start a conversation</div>
                        </div>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    {iframeLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg z-10">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-3 border-[#D97642] border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-[13px] text-[#718096] font-medium">Loading form...</p>
                        </div>
                      </div>
                    )}
                    <iframe
                      src="https://api.leadconnectorhq.com/widget/form/l6m1l2C1ZbhWcuOBNDGB"
                      style={{ width: '100%', height: '420px', border: 'none', borderRadius: '8px' }}
                      id="inline-l6m1l2C1ZbhWcuOBNDGB"
                      data-layout="{'id':'INLINE'}"
                      data-trigger-type="alwaysShow"
                      data-trigger-value=""
                      data-activation-type="alwaysActivated"
                      data-activation-value=""
                      data-deactivation-type="neverDeactivate"
                      data-deactivation-value=""
                      data-form-name="Wealth Strategy Quick Contact"
                      data-height="420"
                      data-layout-iframe-id="inline-l6m1l2C1ZbhWcuOBNDGB"
                      data-form-id="l6m1l2C1ZbhWcuOBNDGB"
                      title="Wealth Strategy Quick Contact Form"
                      onLoad={() => setIframeLoading(false)}
                      onError={() => {
                        setIframeError(true);
                        setIframeLoading(false);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Phone CTA below card */}
            <div className="mt-5 flex items-center justify-center gap-3 bg-white rounded-xl px-6 py-4 border border-gray-100 shadow-sm">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                <i className="ri-phone-fill text-[18px] text-[#1A3A52]"></i>
              </div>
              <div>
                <p className="text-[12px] text-[#718096]">Prefer to call us directly?</p>
                <a href="tel:+13176482983" className="text-[15px] font-bold text-[#1A3A52] hover:text-[#D97642] transition-colors whitespace-nowrap">
                  (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA Strip ── */}
      <section className="bg-[#1A3A52] py-12">
        <div className="max-w-[700px] mx-auto px-6 text-center">
          <h2 className="text-[24px] md:text-[30px] font-bold text-white mb-3">
            Your family's financial future starts with one conversation.
          </h2>
          <p className="text-[15px] text-white/75 mb-6">
            No products. No pressure. Just clarity — in 15 minutes.
          </p>
          <a
            href="#top"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all shadow-lg cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-calendar-check-fill text-[18px]"></i>
            </div>
            Book My Free Call Now
          </a>
        </div>
      </section>

      {/* ── Minimal Footer ── */}
      <footer className="bg-[#F0EDE8] border-t border-gray-200 py-6">
        <div className="max-w-[1100px] mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[12px] text-[#718096]">
            © {new Date().getFullYear()} Hoosier Life Insurance · Indianapolis, IN · Licensed in Indiana
          </p>
          <Link to="/" className="text-[12px] text-[#718096] hover:text-[#D97642] transition-colors whitespace-nowrap">
            ← Back to main site
          </Link>
        </div>
      </footer>
    </div>
  );
}